import { NavigateFunction, Location } from 'react-router-dom';
import { captureEvent } from '../../utils/analytics';
import { defaultConfig } from '../../utils/config';
import { SubscriptionType } from '../../backend/useCredits';

interface User {
  id: string;
  email: string;
}

// Check if user is eligible for free trial
// Returns true if ENABLE_FREE_TRIAL is enabled AND user is not already in a trial
export const shouldShowTrial = (subscription: SubscriptionType): boolean => {
  return defaultConfig.ENABLE_FREE_TRIAL && subscription !== 'trial';
};

// Get button text for pricing page based on trial eligibility
export const getTrialButtonText = (user: User | null, isLoading: boolean, subscription: SubscriptionType): string => {
  if (!user || isLoading) {
    return shouldShowTrial(subscription) ? "Start Free Trial" : "Get Started";
  }
  return ""; // Will be overridden by specific subscription state in component
};

// Get pricing text for hobby plan
export const getHobbyPriceText = (subscription: SubscriptionType): string => {
  if (shouldShowTrial(subscription)) {
    return "20 free credits.\nThen $9.99/month.";
  }
  return "$9.99/month";
};

// Get pricing text for creator plan
export const getCreatorPriceText = (subscription: SubscriptionType): string => {
  if (shouldShowTrial(subscription)) {
    return "20 free credits.\nThen $24.99/month.";
  }
  return "$24.99/month";
};

// Get modal button text
export const getModalButtonText = (subscription: SubscriptionType): string => {
  if (shouldShowTrial(subscription)) {
    return " Start Free Trial ";
  }
  return " Subscribe ";
};

// Get button text for logged-in users on pricing cards
export const getLoggedInButtonText = (
  subscription: SubscriptionType,
  planName: 'hobby' | 'creator' | 'ultimate'
): string => {
  // If user has the current plan, show manage
  if (subscription === planName) {
    return "Manage Plan";
  }
  
  // If user has a higher plan, show nothing
  if (planName === 'hobby' && (subscription === 'creator' || subscription === 'ultimate')) {
    return "";
  }
  
  // Otherwise show upgrade (pricing page should never show "Start Free Trial")
  return "Upgrade";
};

// Redirect to signup page if user is not logged in
export const redirectToSignup = (navigate: NavigateFunction, location: Location) => {
  // console.log('redirecting from pricing to signup');
  const redirectPath = localStorage.getItem('redirectPath');
  if (!redirectPath || !redirectPath.includes('stripe')) {
    localStorage.setItem('redirectPath', location.pathname);
  }
  navigate('/signup', { state: { from: location } });
};

export const onClickHobby = (user: User | null, navigate: NavigateFunction, location: Location, subscription: SubscriptionType, isTrial: boolean = false) => {
  // console.log('onClickHobby');
  // Use trial link ONLY if explicitly requested via isTrial parameter
  const useTrial = isTrial;
  const url_beginning = useTrial 
    ? (process.env.REACT_APP_STRIPE_PAYMENT_PAGE_HOBBY_TRIAL || process.env.REACT_APP_STRIPE_PAYMENT_PAGE_HOBBY)
    : process.env.REACT_APP_STRIPE_PAYMENT_PAGE_HOBBY;

  if (!user) {
    localStorage.setItem('redirectPath', url_beginning);
    // console.log('Storing redirect path in onClickHobby:', url_beginning);
    redirectToSignup(navigate, location);
    return;
  }
  
  captureEvent('stripe clicked');
  const url = `${url_beginning}?prefilled_email=${user?.email}&client_reference_id=${user?.id}`; 
  // Navigate in the same tab instead of opening a new one
  window.location.href = url;
};

export const onClickCreator = (user: User | null, navigate: NavigateFunction, location: Location, subscription: SubscriptionType, isTrial: boolean = false) => {
  // console.log('onClickCreator');
  // Use trial link ONLY if explicitly requested via isTrial parameter
  const useTrial = isTrial;
  const url_beginning = useTrial
    ? (process.env.REACT_APP_STRIPE_PAYMENT_PAGE_CREATOR_TRIAL || process.env.REACT_APP_STRIPE_PAYMENT_PAGE_CREATOR)
    : process.env.REACT_APP_STRIPE_PAYMENT_PAGE_CREATOR;

  if (!user) {
    localStorage.setItem('redirectPath', url_beginning);
    // console.log('Storing redirect path in onClickCreator:', url_beginning);
    redirectToSignup(navigate, location);
    return;
  }
  
  captureEvent('stripe clicked');
  const url = `${url_beginning}?prefilled_email=${user?.email}&client_reference_id=${user?.id}`; 
  // Navigate in the same tab instead of opening a new one
  window.location.href = url;
};

export const onClickUltimate = (user: User | null, navigate: NavigateFunction, location: Location) => {
  // TODO
  if (!user) {
    redirectToSignup(navigate, location);
    return;
  }
  
  // console.log('onClickUltimate');
  captureEvent('stripe clicked');
};

export const handleCreatorPlanChange = (event: any, setSelectedCreatorPlan: (value: string) => void) => {
  // Extract the value from the event
  const option = event.target.value;
  setSelectedCreatorPlan(option);
  // console.log('Selected creator plan:', option);
};

export const getCreatorPrice = (selectedCreatorPlan: string): string => {
  switch(selectedCreatorPlan) {
    case "35x": return "$29.99";
    case "45x": return "$34.99";
    case "55x": return "$39.99";
    default: return "$24.99"; // 25x is default
  }
};
